// Ported from dev-scrips-pm-react src/documents/components/documents-pager.tsx (PROD-1119 documents module).
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * DocumentsPager — page-based pagination footer for the patient
 * documents list.
 *
 * Backend ships a flat array with no totalCount envelope, so we use
 * the same "next enabled when page is full" heuristic as the chart-tab
 * documents list (`patients/components/chart/documents-chart-tab.tsx:104-107`):
 *   - Prev enabled when `page > 1`
 *   - Next enabled when `pageFull` (caller computes `data.length === pageSize`)
 *   - When result count < pageSize, Next disables — that's the last page
 *
 * No total-pages display: the wire doesn't expose totalNumberOfPages
 * for this endpoint. Showing "page N of ?" would invent a number.
 *
 * Disabled-state styling matches the canonical pager pattern in
 * `claim-workbench-page.tsx`'s footer.
 *
 * DS reconciliation (PROD-1119): shadcn Button → DS Button.
 * `variant="outline"` has no DS equivalent → `variant="secondary"`
 * (closest neutral, per shadcn→DS mapping §1).
 */

export interface DocumentsPagerProps {
  page: number;
  /** Caller-computed: `data?.length === pageSize`. */
  pageFull: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Optional — disables both buttons during refetch. */
  isFetching?: boolean;
}

export function DocumentsPager({
  page,
  pageFull,
  onPrev,
  onNext,
  isFetching,
}: DocumentsPagerProps) {
  const prevDisabled = page <= 1 || !!isFetching;
  const nextDisabled = !pageFull || !!isFetching;

  return (
    <nav
      aria-label="Documents pagination"
      className="flex items-center justify-end gap-3 py-2"
    >
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={onPrev}
        disabled={prevDisabled}
        aria-label="Previous page"
        className="gap-1"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Prev
      </Button>
      <span
        className={cn(
          'text-sm tabular-nums',
          isFetching ? 'text-text-muted' : 'text-text-secondary',
        )}
      >
        Page <span className="font-medium text-text-primary">{page}</span>
      </span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={onNext}
        disabled={nextDisabled}
        aria-label="Next page"
        className="gap-1"
      >
        Next
        <ChevronRight className="size-4" aria-hidden />
      </Button>
    </nav>
  );
}
